import { z } from 'zod';
import { criarCifSchema, rateioCompetenciaParam } from './rateio.js';
import { documentoParceiroSchema } from './parceiros.js';
import { criarCustoAquisicaoSchema } from './custos-aquisicao.js';
import { criarItemSchema } from './itens.js';
import { publicarSchema } from './liberacao-preco.js';
import { orcamentoSchema } from './orcamentos.js';
import { criarPedidoSchema } from './plano.js';
import type { Permissao } from './permissoes.js';
import { cuid, dinheiro } from './primitives.js';

/**
 * Copiloto que executa acoes (Sessao 23, docs/COPILOTO-ACOES.md). A IA so PROPOE: a API devolve uma previa e um token de uso unico;
 * so o clique do usuario (POST /copiloto/acoes/:id/confirmar) executa. Dinheiro e taxa: string decimal.
 */

export const TIPOS_ACAO = ['LEITURA', 'ESCRITA', 'SENSIVEL'] as const;
export type TipoAcaoDto = (typeof TIPOS_ACAO)[number];

/** Lista fechada de telas que `abrir_tela` aceita, com a permissao de cada uma (null = todos). Espelha o menu (teste no web confere). */
export const ROTAS_COPILOTO: Readonly<Record<string, Permissao | null>> = {
  '/': null,
  '/precificar/formacao': 'precificacao.simular',
  '/precificar/lista': 'lista.ver',
  '/precificar/simulador': 'precificacao.simular',
  '/precificar/margens': 'margens.ver',
  '/vender/orcamentos': 'orcamento.ver',
  '/clientes': 'cliente.ver',
  '/plano': 'plano.ver',
  '/plano/carteira': 'carteira.ver',
  '/plano/compras': 'plano.ver',
  '/plano/necessidade': 'plano.ver',
  '/plano/custo-fabrica': 'plano.ver',
  '/plano/fluxo': 'plano.ver',
  '/custos/fichas-tecnicas': 'ficha.ver',
  '/custos/centros': 'centro.ver',
  '/custos/atualizacoes': 'lote.ver',
  '/custos/pessoal': 'pessoal.ver',
  '/custos/criterios': 'centro.ver',
  '/custos/configuracoes-rateio': 'rateio.ver',
  '/custos/cifs': 'rateio.ver',
  '/custos/itens-cif': 'rateio.ver',
  '/custos/rateios': 'rateio.ver',
  '/cadastros/itens': 'item.ver',
  '/cadastros/fornecedores': 'custo.ver',
  '/cadastros/imobilizados': 'centro.ver',
  '/cadastros/pessoas': 'rh.ver',
  '/cadastros/cargos': 'rh.ver',
  '/cadastros/tabelas': 'tabela.ver',
  '/cadastros/condicoes': 'tabela.ver',
  '/configuracoes/empresa': 'empresa.ver',
  '/configuracoes/reforma': 'parametros.ver',
  '/cadastros/categorias': 'item.ver',
  '/cadastros/unidades': 'item.ver',
  '/configuracoes/plano': 'plano.ver',
  '/configuracoes/integracoes': 'integracoes.gerenciar',
  '/configuracoes/onboarding': 'onboarding.executar',
  '/ajuda': null,
};
/** Telas com id (a API confere o registro no servico). */
export const ROTAS_COPILOTO_COM_ID: Readonly<Record<string, Permissao>> = {
  '/vender/orcamentos/$id': 'orcamento.ver',
  '/cadastros/itens/$id': 'item.ver',
  '/custos/atualizacoes/$id': 'lote.ver',
};
export const ROTAS_ABRIR_TELA = [...Object.keys(ROTAS_COPILOTO)] as [string, ...string[]];

/** Chaves de consulta (prefixos do TanStack Query) que o front invalida depois de executar. */
export const CHAVES_INVALIDACAO = ['orcamentos', 'orcamento', 'clientes', 'itens', 'custos-aquisicao', 'lotes', 'cifs', 'carteira', 'precificacao', 'rateios'] as const;
export type ChaveInvalidacao = (typeof CHAVES_INVALIDACAO)[number];

// ---------- Entradas das acoes compostas ----------

const quantidade = dinheiro;

export const criarOrcamentoAcaoSchema = orcamentoSchema.omit({ options: true }).extend({ data: orcamentoSchema.shape.data.optional() });
export type CriarOrcamentoAcao = z.infer<typeof criarOrcamentoAcaoSchema>;

export const operacaoItemSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('adicionar'), itemId: cuid, quantidade, precoUnitario: dinheiro }),
  z.object({ op: z.literal('remover'), linha: z.number().int().min(1).max(500) }),
  z
    .object({ op: z.literal('alterar'), linha: z.number().int().min(1).max(500), quantidade: quantidade.optional(), precoUnitario: dinheiro.optional() })
    .refine((v) => v.quantidade !== undefined || v.precoUnitario !== undefined, 'Informe quantidade ou preco'),
]);
export type OperacaoItem = z.infer<typeof operacaoItemSchema>;
export const alterarItensRascunhoSchema = z.object({ orcamentoId: cuid, operacoes: z.array(operacaoItemSchema).min(1).max(20) });
export type AlterarItensRascunhoAcao = z.infer<typeof alterarItensRascunhoSchema>;

export const gerarNovaVersaoSchema = z.object({ orcamentoId: cuid, comCustosAtuais: z.boolean().default(false) });
export type GerarNovaVersaoAcao = z.infer<typeof gerarNovaVersaoSchema>;

export const versaoAcaoSchema = z.object({ orcamentoId: cuid, versao: z.number().int().min(1).max(9999).optional() });
export type VersaoAcao = z.infer<typeof versaoAcaoSchema>;

export const criarItemMercadoriaSchema = z.object({
  item: criarItemSchema.extend({ tipo: z.literal('MERCADORIA') }),
  custo: criarCustoAquisicaoSchema.omit({ itemId: true }),
});
export type CriarItemMercadoriaAcao = z.infer<typeof criarItemMercadoriaSchema>;

/** Sessao 30: o Copiloto cria o cliente (papel CLIENTE de um parceiro) so com os campos basicos; o resto se completa na tela. */
export const criarClienteAcaoSchema = z.object({
  nome: z.string().trim().min(1).max(200),
  documento: documentoParceiroSchema.optional(),
  uf: z.string().regex(/^[A-Z]{2}$/, 'UF com 2 letras maiusculas').optional(),
  municipio: z.string().trim().min(1).max(100).optional(),
});

export { criarCifSchema as lancarCifSchema, criarPedidoSchema as criarPedidoAcaoSchema, publicarSchema as publicarListaAcaoSchema, rateioCompetenciaParam as rateioAcaoSchema };

export const abrirTelaSchema = z.object({
  rota: z.string().max(100),
  filtros: z.record(z.string().regex(/^[A-Za-z]{1,30}$/), z.string().max(100)).refine((f) => Object.keys(f).length <= 10, 'Filtros demais').optional(),
});

// ---------- Previa, cartao, confirmacao ----------

export type FaixaLucroDto = 'green' | 'orange' | 'red';

export interface PreviaItem {
  codigo: string;
  descricao: string;
  quantidade: string;
  precoUnitario: string;
  total: string;
  /** Fracao; nunca para REPRESENTANTE. */
  lucro?: string;
  faixa?: FaixaLucroDto;
}

/** O que sera gravado, calculado pelo servidor (nunca texto do modelo). `estado` (carimbos que entram no hash) nao sai da API. */
export interface PreviaAcao {
  titulo: string;
  /** `sensivel`: o campo so aparece para quem tem custo.ver ('custo') ou lucro.ver ('lucro'); o servidor refaz o corte a cada leitura do cartao. */
  campos: Array<{ rotulo: string; valor: string; sensivel?: 'custo' | 'lucro' }>;
  itens?: PreviaItem[];
  totais?: { total: string; lucro?: string; faixa?: FaixaLucroDto; itens: number };
  avisos: Array<{ code: string; message: string }>;
  /** SENSIVEL: o que fica imutavel/irreversivel. */
  impacto?: string[];
}

export type StatusAcao = 'PENDENTE' | 'EXECUTADA' | 'CANCELADA' | 'EXPIRADA';

export interface CopilotoAcaoCartao {
  id: string;
  acao: string;
  tipo: 'ESCRITA' | 'SENSIVEL';
  previa: PreviaAcao;
  status: StatusAcao;
  expiraEm: string;
  /** SENSIVEL: a palavra que o usuario digita para habilitar o Confirmar. */
  palavra?: string;
  resultado?: { resumo: string; navegarPara: string };
  /** EXPIRADA por motivo que nao e o prazo (ex.: cartao de versao anterior): codigo estavel e a frase para o usuario. */
  motivo?: { codigo: string; mensagem: string };
}

/** Cartao do SSE ao vivo: o token so existe aqui e na resposta de `/previa`. */
export type CopilotoAcaoCartaoComToken = CopilotoAcaoCartao & { token: string };

export const confirmarAcaoSchema = z.object({
  token: z.string().min(20).max(200),
  reforcada: z.boolean().optional(),
  palavra: z.string().max(40).optional(),
});
export type ConfirmarAcaoDto = z.infer<typeof confirmarAcaoSchema>;

export interface ConfirmarAcaoResponse {
  status: 'EXECUTADA';
  resultado: { registroId: string | null; resumo: string };
  navegarPara: string;
  invalidar: ChaveInvalidacao[];
}
